/**
 * Payroll Bridge — werkgeverkant (Mijn Cockpit tab 7 + Menu Data).
 * De server ontvangt nooit e-mailadressen, namen, BSN of salarissen: de
 * wizard hasht en rekent lokaal in de browser en stuurt alleen het resultaat.
 */
import api from './api';

export type AllocatieModus = 'vast' | 'percentage' | 'maatwerk';
export type FreshnessNiveau = 'geen' | 'ok' | 'waarschuwing' | 'verlopen';

export interface Freshness {
  laatste_import_op: string | null;
  dagen_oud: number | null;
  niveau: FreshnessNiveau;
  data_lock: boolean;
  waarschuwing_vanaf_dagen: number;
  lock_vanaf_dagen: number;
}

export interface KolomMapping {
  email: string | null;
  fte: string | null;
  afdeling: string | null;
  salaris?: string | null;
  pro_budget?: string | null;
  hobby_budget?: string | null;
  geslacht?: string | null;
  geboortedatum?: string | null;
}

export interface PayrollConfig {
  employer_id: string;
  kolom_mapping: Partial<KolomMapping>;
  allocatie_modus: AllocatieModus;
  pro_vast_bedrag: number | null;
  hobby_vast_bedrag: number | null;
  pro_pct: number | null;
  hobby_pct: number | null;
  laatste_import_op: string | null;
  laatste_import_aantal: number | null;
  laatste_import_bestandsnaam: string | null;
}

export interface PayrollStatus {
  config: PayrollConfig | null;
  freshness: Freshness;
  boekjaar: number;
  wallets: {
    actief: number;
    inactief: number;
    gekoppeld_aan_account: number;
    fte_som: number;
    pro_totaal: number;
    hobby_totaal: number;
    pro_resterend: number;
    hobby_resterend: number;
  };
  afdelingen: Array<{
    label: string | null;
    department_id: string | null;
    department_naam: string | null;
    gekoppeld: boolean;
    aantal: number;
    fte_som: number;
    onder_privacy_grendel: boolean;
  }>;
}

export interface ImportRij {
  email_hash: string;
  /** SHA-256(employer_id | e-mail) — koppelt de wallet aan ziekmeldingen uit de Ziekmeldings-Wizard. */
  case_hash: string | null;
  fte: number | null;
  afdeling: string | null;
  pro_budget: number;
  hobby_budget: number;
  /** Lokaal afgeleid (migratie 181); de geboortedatum zelf gaat nooit mee. */
  geslacht?: 'man' | 'vrouw' | 'non_binair' | null;
  leeftijdsband?: 'lt25' | '25_40' | '41_55' | 'gt55' | null;
}

/** Geslachtskolom uit Nmbrs/AFAS/Raet/Loket → code; onbekend = null. */
export function geslachtCode(v: unknown): ImportRij['geslacht'] {
  const s = String(v ?? '').trim().toLowerCase();
  if (!s) return null;
  if (['m', 'man', 'male', 'mannelijk', 'heer', 'dhr', 'dhr.'].includes(s)) return 'man';
  if (['v', 'vrouw', 'f', 'female', 'vrouwelijk', 'mevrouw', 'mevr', 'mevr.'].includes(s)) return 'vrouw';
  if (['x', 'o', 'non-binair', 'non_binair', 'nonbinair', 'anders', 'other', 'divers'].includes(s)) return 'non_binair';
  return null;
}

/** Geboortedatum (dd-mm-jjjj, jjjj-mm-dd, Excel-serieel of Date) → leeftijdsband op `peildatum`. */
export function leeftijdsband(v: unknown, peildatum = new Date()): ImportRij['leeftijdsband'] {
  let d: Date | null = null;
  if (v instanceof Date) d = v;
  else if (typeof v === 'number' && v > 1000 && v < 80000) d = new Date(Date.UTC(1899, 11, 30) + v * 86_400_000);
  else {
    const s = String(v ?? '').trim();
    let m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (m) d = new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
    else if ((m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/))) d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  }
  if (!d || Number.isNaN(d.getTime())) return null;
  let leeftijd = peildatum.getUTCFullYear() - d.getUTCFullYear();
  if (peildatum.getUTCMonth() < d.getUTCMonth() || (peildatum.getUTCMonth() === d.getUTCMonth() && peildatum.getUTCDate() < d.getUTCDate())) leeftijd--;
  if (leeftijd < 14 || leeftijd > 100) return null;
  return leeftijd < 25 ? 'lt25' : leeftijd <= 40 ? '25_40' : leeftijd <= 55 ? '41_55' : 'gt55';
}

export interface ImportInvoer {
  bestandsnaam: string | null;
  kolom_mapping: KolomMapping;
  allocatie: {
    modus: AllocatieModus;
    pro_vast_bedrag?: number | null;
    hobby_vast_bedrag?: number | null;
    pro_pct?: number | null;
    hobby_pct?: number | null;
  };
  rijen: ImportRij[];
}

export interface ImportResultaat {
  nieuw: number;
  bijgewerkt: number;
  geinactiveerd: number;
  overgeslagen: number;
  fouten: string[];
  afdelingen_aangemaakt: string[];
  afdelingen_niet_gekoppeld: string[];
}

export interface VerzuimMaand {
  jaar: number;
  maand: number;
  verzuim_pct: number | null;
  fte_som: number | null;
  aantal_actief: number | null;
  aantal_ziekmeldingen: number | null;
  ziekte_fte_dagen: number | null;
  kalenderdagen: number;
  berekend_op: string | null;
}

export interface VerzuimOverzicht {
  freshness: Freshness;
  data_lock: boolean;
  bridge_actief: boolean;
  min_groep: number;
  huidig_pct: number | null;
  trend: VerzuimMaand[];
  per_afdeling: Array<{
    department_id: string;
    naam: string;
    laatste_pct: number | null;
    onder_privacy_grendel: boolean;
    reeks: Array<{ jaar: number; maand: number; verzuim_pct: number | null; aantal_actief: number }>;
  }>;
}

const d = <T,>(r: { data: { data: T } }) => r.data.data;

export const PAYROLL_QUERY_KEY = ['employer-payroll-bridge'];
export const VERZUIM_QUERY_KEY = ['employer-payroll-verzuim'];

export const getPayrollStatus = () => api.get<{ data: PayrollStatus }>('/employer/medewerkersportaal/payroll').then(d);

export const importeerPayroll = (invoer: ImportInvoer) =>
  api.post<{ data: ImportResultaat }>('/employer/medewerkersportaal/payroll/import', invoer).then(d);

export const getVerzuimOverzicht = () => api.get<{ data: VerzuimOverzicht }>('/employer/medewerkersportaal/payroll/verzuim').then(d);

/** SHA-256 in de browser (Web Crypto) — zelfde normalisatie als de server: trim + lowercase. */
export async function hashEmailLokaal(email: string): Promise<string> {
  const data = new TextEncoder().encode(email.trim().toLowerCase());
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
